// Localização: frontend/src/components/Prontuario/StatusFilterButton.jsx
// Componente reutilizável para botão de filtro de status (padronizado)

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Tag } from 'lucide-react';
import CollapsibleStatusFilter from '../Financeiro/CollapsibleStatusFilter';

/**
 * Componente de Botão de Filtro de Status
 * Padronizado para uso em todas as abas do prontuário
 * 
 * @param {Object} props
 * @param {Array} props.status - Array de status selecionados
 * @param {Function} props.onStatusChange - Callback quando o status muda
 * @param {string} props.type - Tipo de conta ('pagar' ou 'receber')
 * @param {string} props.className - Classes CSS adicionais
 */
const StatusFilterButton = ({
    status = [],
    onStatusChange,
    type = 'receber',
    className = ''
}) => {
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const statusButtonRef = useRef(null);
    const statusFilterRef = useRef(null);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleClickOutside = (event) => {
            if (
                statusFilterRef.current &&
                statusButtonRef.current &&
                !statusFilterRef.current.contains(event.target) &&
                !statusButtonRef.current.contains(event.target)
            ) {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showStatusFilter]);

    // Fechar dropdown ao pressionar Escape
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showStatusFilter]);

    const handleStatusChange = useCallback((newStatus) => {
        if (onStatusChange) {
            onStatusChange(newStatus || []);
        }
    }, [onStatusChange]);

    const hasActiveFilters = status && status.length > 0;
    const statusLabel = hasActiveFilters 
        ? `${status.length} ${status.length === 1 ? 'status' : 'status'}`
        : 'Status';

    return (
        <div className={`relative ${className}`} ref={statusButtonRef}>
            <button
                onClick={() => setShowStatusFilter(!showStatusFilter)}
                className={`
                    flex items-center justify-center gap-2
                    px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg
                    transition-all duration-200 font-medium 
                    text-xs sm:text-sm
                    border shadow-sm hover:shadow-md
                    ${showStatusFilter || hasActiveFilters
                        ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-md font-semibold' 
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-300 hover:bg-gray-50'
                    }
                `}
                title={showStatusFilter ? 'Ocultar filtro de status' : 'Mostrar filtro de status'}
            >
                <Tag className={`h-4 w-4 flex-shrink-0 ${showStatusFilter ? 'scale-110' : ''} transition-transform`} />
                <span className="hidden sm:inline whitespace-nowrap">{statusLabel}</span>
            </button>

            {/* Dropdown do Filtro de Status */}
            {showStatusFilter && (
                <div
                    ref={statusFilterRef}
                    className="absolute top-full right-0 mt-2 w-[250px] max-w-[calc(100vw-2rem)] sm:max-w-[250px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden animate-fadeIn"
                    style={{
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}
                >
                    <div className="p-3 sm:p-4">
                        <CollapsibleStatusFilter
                            status={status}
                            onStatusChange={handleStatusChange}
                            type={type}
                            showOnlyContent={true}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatusFilterButton;

